import React, { useState, useMemo } from 'react';
import { Lightbulb, Search, ArrowRight, Calendar, Heart, ChevronDown } from 'lucide-react';

const pillStyle = { borderRadius: '9999px' };
const cardStyle = { borderRadius: '32px' };

const BankIdeasSection = ({ ideas = [], theme, isDarkMode, handleNavigate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('All');
  const [isYearOpen, setIsYearOpen] = useState(false);

  const formatReadableDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        const year = parts[0];
        const monthNum = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const malayMonths = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
          'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];
        return `${day} ${malayMonths[monthNum]} ${year}`;
      }
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' });
      }
    } catch (e) { /* ignore */ }
    return dateStr;
  };

  const { availableYears, yearCounts } = useMemo(() => {
    const counts = {};
    let totalYrs = 0;
    
    ideas.filter(i => i.status === 'published').forEach(idea => {
      let year = '';
      if (idea.date) {
        const parts = idea.date.split('-');
        if (parts.length === 3 && parts[0].length === 4) {
          year = parts[0];
        } else {
          const d = new Date(idea.date);
          if (!isNaN(d.getTime())) {
            year = d.getFullYear().toString();
          }
        }
      }
      if (year) {
        counts[year] = (counts[year] || 0) + 1;
        totalYrs++;
      }
    });
    
    counts['All'] = totalYrs;
    return {
      availableYears: Object.keys(counts).filter(k => k !== 'All').sort((a, b) => b - a),
      yearCounts: counts
    };
  }, [ideas]);

  const publishedIdeas = useMemo(() => {
    return ideas
      .filter(i => i.status === 'published')
      .filter(i => {
        if (selectedYear === 'All') return true;
        let year = '';
        if (i.date) {
          const parts = i.date.split('-');
          if (parts.length === 3 && parts[0].length === 4) year = parts[0];
          else {
            const d = new Date(i.date);
            if (!isNaN(d.getTime())) year = d.getFullYear().toString();
          }
        }
        return year === selectedYear;
      })
      .filter(i => !searchQuery || i.title?.toLowerCase().includes(searchQuery.toLowerCase()) || i.description?.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => (b.updatedAt || b.createdAt) - (a.updatedAt || a.createdAt));
  }, [ideas, searchQuery, selectedYear]);

  return (
    <section className="animate-in fade-in slide-in-from-bottom-4 duration-1000 space-y-10">
      {/* Header */}
      <div>
        <h1 className={`text-3xl md:text-4xl font-serif ${theme.text}`}>Bank Idea</h1>
        <p className={`text-[10px] font-bold uppercase tracking-widest opacity-40 mt-1 ${theme.text}`}>
          A repository of thoughts and sparks
        </p>
      </div>

      {/* Search */}
      <div style={pillStyle} className={`flex items-center gap-3 px-5 py-3 border ${isDarkMode ? 'border-stone-800 bg-stone-900' : 'border-stone-100 bg-white'}`}>
        <Search size={14} className="opacity-40" />
        <input
          type="text"
          placeholder="Search ideas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`flex-1 bg-transparent text-xs outline-none ${theme.text} placeholder:opacity-30`}
        />
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Year Filter Dropdown */}
        {availableYears.length > 0 && (
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setIsYearOpen(prev => !prev)}
              style={pillStyle}
              className={`px-4 py-1.5 text-[9px] font-bold uppercase tracking-widest transition-all border flex items-center gap-2 ${
                selectedYear !== 'All'
                  ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                  : (isDarkMode ? 'bg-stone-900 border border-stone-850 text-stone-400 hover:text-stone-200' : 'bg-white border-stone-200 text-stone-500 hover:border-stone-400')
              }`}
            >
              <span>{selectedYear === 'All' ? 'All Years' : selectedYear}</span>
              <ChevronDown size={12} className={`${isYearOpen ? 'rotate-180' : ''} transition-transform`} />
            </button>
            
            {isYearOpen && (
              <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300">
                <div className="absolute inset-0 bg-black/20 backdrop-blur-md" onClick={() => setIsYearOpen(false)} />
                <div style={cardStyle} className={`relative w-full max-w-sm mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
                  <div className="flex flex-col gap-2">
                    <p className={`text-[9px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Year</p>
                    {['All', ...availableYears].map(y => (
                      <button 
                        key={y} 
                        onClick={() => { setSelectedYear(y); setIsYearOpen(false); }} 
                        style={pillStyle} 
                        className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${selectedYear === y ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}
                      >
                        <span>{y === 'All' ? 'All Years' : y}</span>
                        <span className="opacity-40 text-[10px]">{yearCounts[y] || 0}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      {publishedIdeas.length === 0 ? (
        <div className="text-center py-20 opacity-40 space-y-4">
          <Lightbulb size={48} className="mx-auto" />
          <p className="text-[10px] font-bold uppercase tracking-widest">No ideas published yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {publishedIdeas.map((idea) => (
            <button
              key={idea.id}
              onClick={() => handleNavigate('bank-idea-view', idea)}
              style={cardStyle}
              className={`group text-left p-6 border transition-all active:scale-[0.98] ${
                isDarkMode ? 'border-stone-800 bg-stone-900/40 hover:bg-stone-800/60' : 'border-stone-100 bg-white hover:shadow-lg'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2 min-w-0 flex-1">
                  <h3 className={`font-serif text-lg leading-tight ${theme.text} group-hover:opacity-80 transition-opacity`}>
                    {idea.title}
                  </h3>
                  {idea.description && (
                    <p className={`text-xs opacity-50 line-clamp-2 ${theme.textMuted}`}>{idea.description}</p>
                  )}
                  <div className="flex items-center flex-wrap gap-2 text-[9px] font-bold uppercase tracking-widest opacity-35">
                    <span>{idea.nodes?.length || 0} nodes</span>
                    {idea.category && (
                      <>
                        <span>•</span>
                        <span>{idea.category}</span>
                      </>
                    )}
                    {(idea.likes || 0) > 0 && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-0.5 text-red-500 opacity-90">
                          <Heart size={10} className="fill-red-500 text-red-500" />
                          <span>{idea.likes}</span>
                        </span>
                      </>
                    )}
                    {idea.date && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar size={10} />
                          {formatReadableDate(idea.date)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <ArrowRight size={16} className={`flex-shrink-0 mt-1 opacity-30 group-hover:opacity-60 transition-opacity ${theme.text}`} />
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

export default BankIdeasSection;
